import { useState, useEffect } from 'react';
import { GravatarService, GravatarProfile } from '@/lib/gravatar';

export function useGravatar(email: string) {
  const [profile, setProfile] = useState<GravatarProfile | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset state when email changes to empty
    if (!email) {
      setAvatarUrl('');
      setProfile(null);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchGravatarData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Get basic avatar URL immediately
        const basicAvatar = GravatarService.getAvatarUrl(email, 200);
        setAvatarUrl(basicAvatar);

        // Try to fetch high-quality avatar and profile
        const [highQualityAvatar, profileData] = await Promise.all([
          GravatarService.getHighQualityAvatar(email, 200),
          GravatarService.getProfile(email)
        ]);

        setAvatarUrl(highQualityAvatar);
        setProfile(profileData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch Gravatar data');
        // Fallback to basic avatar
        setAvatarUrl(GravatarService.getAvatarUrl(email, 200));
      } finally {
        setLoading(false);
      }
    };

    fetchGravatarData();
  }, [email]);

  return {
    profile,
    avatarUrl,
    loading,
    error,
    displayName: profile?.displayName || profile?.name?.formatted || '',
    aboutMe: profile?.aboutMe || '',
    location: profile?.currentLocation || ''
  };
}